/**
 * Step 3 — Preview (the critical intermediary).
 *
 * For EACH selected format × work order, a tab shows the ACTUAL generated output table. Validation
 * warnings highlight yellow, errors red. Each tab shows format + work order + a status chip
 * (OK / warnings / errors). Clicking a cell reveals its column provenance ("This value came from
 * [source field]…"). A validation summary lists every issue with its plain-English message and
 * suggestion. Where feasible, flagged cells are editable inline so a user can correct a value before
 * generating (the edit is a local override applied to the preview; the deterministic run is unchanged —
 * see the note in ConfirmStep).
 */
import { useEffect, useMemo, useState } from "react";
import type { FlowComboPreview, ValidationIssue } from "../../api/generateFlow";
import { Badge } from "../../components/Badge";
import { ProvenanceCell } from "./ProvenanceCell";
import { track, EVENTS } from "./telemetry";

type Status = "ok" | "warnings" | "errors";

function statusTone(s: Status): "success" | "warning" | "danger" {
  return s === "ok" ? "success" : s === "warnings" ? "warning" : "danger";
}

function statusLabel(s: Status): string {
  return s === "ok" ? "Looks good" : s === "warnings" ? "Warnings" : "Errors";
}

/** Build the set of (row, column) cells touched by an error / warning, for highlighting. */
function flaggedCells(issues: ValidationIssue[], severity: "error" | "warning") {
  const set = new Set<string>();
  for (const i of issues) {
    if (i.severity === severity && i.field && i.row != null) {
      set.add(`${i.row}:${i.field.toLowerCase()}`);
    }
  }
  return set;
}

export function PreviewStep({ combos }: { combos: FlowComboPreview[] }) {
  const [active, setActive] = useState(0);

  // a viewed tab fires edd_previewed once per format name
  useEffect(() => {
    const c = combos[active];
    if (c) track(EVENTS.edd_previewed, { format_name: c.config_name, work_order: c.work_order });
  }, [active, combos]);

  if (combos.length === 0) {
    return <p className="muted">Nothing to preview yet.</p>;
  }
  const combo = combos[Math.min(active, combos.length - 1)];

  return (
    <div className="stack">
      <div>
        <h2 style={{ marginBottom: 4 }}>Preview every deliverable</h2>
        <p className="muted" style={{ margin: 0 }}>
          Check each format against each work order before you generate. Yellow cells have a warning;
          red cells have an error. Click any cell to see where its value came from.
        </p>
      </div>

      {/* tab strip: one tab per (format × work order) combo */}
      <div className="row row--wrap" style={{ gap: "var(--space-1)" }} role="tablist">
        {combos.map((c, i) => {
          const s = c.status as Status;
          return (
            <button
              key={`${c.source_index}-${c.config_id ?? c.template ?? c.config_name}-${i}`}
              type="button"
              role="tab"
              aria-selected={i === active}
              className={`chip ${i === active ? "chip--active" : ""}`.trim()}
              onClick={() => setActive(i)}
            >
              <span style={{ marginRight: 6 }}>
                {c.config_name} · {c.work_order}
              </span>
              <Badge tone={statusTone(s)}>{statusLabel(s)}</Badge>
            </button>
          );
        })}
      </div>

      <ComboPanel combo={combo} />
    </div>
  );
}

function ComboPanel({ combo }: { combo: FlowComboPreview }) {
  // local inline edits: "row:col" → new value (preview-only override)
  const [edits, setEdits] = useState<Record<string, string>>({});
  // reset edits when the combo changes
  useEffect(() => setEdits({}), [combo]);

  const provBySource = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of combo.provenance) m[p.column.toLowerCase()] = p.source;
    return m;
  }, [combo.provenance]);

  const issues = combo.validation?.issues ?? [];
  const errorCells = useMemo(() => flaggedCells(issues, "error"), [issues]);
  const warnCells = useMemo(() => flaggedCells(issues, "warning"), [issues]);
  // columns flagged with no specific row (shape issues like a missing/space heading)
  const errorCols = useMemo(
    () =>
      new Set(
        issues
          .filter((i) => i.severity === "error" && i.field && i.row == null)
          .map((i) => (i.field as string).toLowerCase()),
      ),
    [issues],
  );

  if (combo.error) {
    return (
      <div className="banner banner--danger">
        <strong>{combo.config_name} · {combo.work_order}</strong>
        <div>{combo.error}</div>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="row row--between row--wrap" style={{ alignItems: "center" }}>
        <div className="row" style={{ gap: "var(--space-2)", alignItems: "center" }}>
          <strong>{combo.config_name}</strong>
          <Badge tone="accent">{combo.work_order}</Badge>
          <span className="faint">
            {combo.sx_row_count} sample rows
            {combo.qa_row_count > 0 ? ` · ${combo.qa_row_count} QC rows` : ""} ·{" "}
            {combo.output_format}
          </span>
        </div>
        <Badge tone={statusTone(combo.status as Status)}>{statusLabel(combo.status as Status)}</Badge>
      </div>

      {/* the actual generated table — user-resizable (drag the bottom-right corner) with a
          frozen, opaque header row */}
      <div className="table-wrap table-wrap--resizable" style={{ height: 360 }}>
        <table className="data">
          <thead>
            <tr>
              {combo.columns.map((c) => (
                <th
                  key={c}
                  style={errorCols.has(c.toLowerCase()) ? { color: "var(--danger, #c0392b)" } : undefined}
                  title={provBySource[c.toLowerCase()] ?? ""}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {combo.rows.map((row, ri) => (
              <tr key={ri}>
                {combo.columns.map((c) => {
                  const cellKey = `${ri}:${c.toLowerCase()}`;
                  const raw = row[c];
                  const value = edits[cellKey] ?? (raw == null ? "" : String(raw));
                  const isError = errorCells.has(cellKey) || errorCols.has(c.toLowerCase());
                  const isWarn = warnCells.has(cellKey);
                  return (
                    <td key={c}>
                      {isError || isWarn ? (
                        <input
                          className="input"
                          aria-label={`${c} (row ${ri + 1})`}
                          value={value}
                          onChange={(e) =>
                            setEdits((prev) => ({ ...prev, [cellKey]: e.target.value }))
                          }
                          style={{
                            width: "100%",
                            minWidth: 80,
                            background: isError ? "#fde8e8" : "#fdf6e3",
                            border: "1px solid",
                            borderColor: isError ? "#e74c3c" : "#e0b400",
                            borderRadius: 4,
                            padding: "2px 6px",
                          }}
                        />
                      ) : (
                        <ProvenanceCell value={value} source={provBySource[c.toLowerCase()]} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {combo.rows.length === 0 && (
              <tr>
                <td colSpan={Math.max(combo.columns.length, 1)} className="faint">
                  No rows were produced for this work order.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ValidationSummary combo={combo} />
    </div>
  );
}

function ValidationSummary({ combo }: { combo: FlowComboPreview }) {
  const v = combo.validation;
  const issues = v?.issues ?? [];
  const warnings = combo.warnings ?? [];

  if (issues.length === 0 && warnings.length === 0) {
    return (
      <div className="banner banner--info">
        No validation issues found{v?.spec_name ? ` against ${v.spec_name}` : ""}. This deliverable is
        ready.
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: "var(--space-1)" }}>
      <strong>
        Validation
        {v?.summary
          ? ` — ${v.summary.errors} error${v.summary.errors === 1 ? "" : "s"}, ${v.summary.warnings} warning${v.summary.warnings === 1 ? "" : "s"}`
          : ""}
        {v?.spec_name ? ` (against ${v.spec_name})` : ""}
      </strong>
      {warnings.map((w, i) => (
        <div key={`w-${i}`} className="banner banner--warning">
          {w}
        </div>
      ))}
      {issues.map((iss, i) => (
        <div
          key={`i-${i}`}
          className={`banner ${iss.severity === "error" ? "banner--danger" : "banner--warning"}`}
        >
          <div>
            {iss.field ? <strong>{iss.field}: </strong> : null}
            {iss.message}
          </div>
          {iss.suggestion && <div className="faint">{iss.suggestion}</div>}
        </div>
      ))}
    </div>
  );
}
